import React, { useEffect, useState } from 'react';
import {
  Sparkles,
  Zap,
  Code,
  CheckCircle,
  GitPullRequest,
  BookOpen,
  Crown,
  Rocket,
  Search,
  X,
  ArrowRight,
  Shield,
  FileText,
  Star,
  Check,
  AlertCircle,
  TestTube
} from 'lucide-react';
import {
  apiGetProducts,
  apiGetSubscriptionCheckoutSession,
  ProductItemResponse,
  ProductsResponse
} from '@/services/ProductSubscriptionService';

interface EnhancedSubscriptionModalDarkProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
}

const EnhancedSubscriptionModalDark: React.FC<EnhancedSubscriptionModalDarkProps> = ({
  isOpen,
  onClose,
  projectId
}) => {
  const [productsResponse, setProductsResponse] = useState<ProductsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectingProduct, setSelectingProduct] = useState<string | null>(null);
  const [intervals, setIntervals] = useState<string[]>([]);
  const [activeInterval, setActiveInterval] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
    }
  }, [isOpen]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await apiGetProducts<ProductsResponse>();
      if (response) {
        setProductsResponse(response.data);

        if (response.data.productType === 'SUBSCRIPTION') {
          const products = response.data.products;
          const allIntervals = new Set<string>();
          products.forEach(product => {
            if (product.interval) {
              allIntervals.add(product.interval);
            }
          });

          const intervalsList = [...allIntervals].sort((a, b) => {
            if (a.toLowerCase().includes('month') && b.toLowerCase().includes('year')) return -1;
            if (a.toLowerCase().includes('year') && b.toLowerCase().includes('month')) return 1;
            return a.localeCompare(b);
          });

          setIntervals(intervalsList);
          const monthlyInterval = intervalsList.find(i => i.toLowerCase().includes('month'));
          setActiveInterval(monthlyInterval || intervalsList[0] || '');
        }
      }
    } catch (err: any) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = async (product: ProductItemResponse) => {
    try {
      setSelectingProduct(product.priceId);

      // Store project ID before redirecting to Stripe
      if (projectId) {
        localStorage.setItem('returnToProjectId', projectId);
      }

      const response = await apiGetSubscriptionCheckoutSession(product.priceId);

      if (response && response.data) {
        if (typeof response.data === 'string') {
          window.location.href = response.data;
        } else if (typeof response.data === 'object' && response.data !== null && 'url' in response.data) {
          window.location.href = (response.data as { url: string }).url;
        }
      }
    } catch (err: any) {
      console.error('Unable to create checkout session:', err);
    } finally {
      setSelectingProduct(null);
    }
  };

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('fr-Fr', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount / 100);
  };

  const getPackageDetails = (plan: ProductItemResponse) => {
    // Map plan names to UI details
    const planName = plan.name.toLowerCase();

    if (planName.includes('startup')) {
      return {
        icon: Rocket,
        accentColor: "#00FF88",
        gradient: "from-emerald-500 to-green-600",
        tagline: "Build & Scale Fast.",
        features: [
          `${plan.credits.toLocaleString()} credits`,
          "Priority queue processing",
          "All code-generation models",
          "Model improvements",
          "Email support"
        ]
      };
    } else if (planName.includes('business')) {
      return {
        icon: Code,
        accentColor: "#8B5CF6",
        gradient: "from-purple-500 to-violet-600",
        tagline: "Enterprise Power.",
        features: [
          `${plan.credits.toLocaleString()} credits`,
          "Premium Fast AI servers",
          "Priority PR processing",
          "All model improvements",
          "4-hour support SLAs"
        ]
      };
    } else if (planName.includes('enterprise')) {
      return {
        icon: Crown,
        accentColor: "#F59E0B",
        gradient: "from-amber-500 to-orange-600",
        tagline: "Ultimate Scale.",
        features: [
          `${plan.credits.toLocaleString()} credits`,
          "Premium Fast AI servers",
          "Highest priority processing",
          "Dedicated support",
          "Custom SLAs"
        ]
      };
    }

    // Default fallback
    return {
      icon: Search,
      accentColor: "#00D9FF",
      gradient: "from-cyan-500 to-blue-600",
      tagline: "Get Started.",
      features: [
        `${plan.credits.toLocaleString()} credits`,
        "All code-generation models",
        "Credit usage tracking",
        "Email support"
      ]
    };
  };

  if (!isOpen) return null;

  const products = productsResponse?.products || [];
  const filteredProducts = activeInterval
    ? products.filter(p => p.interval === activeInterval)
    : products;
  const sortedProducts = [...filteredProducts].sort((a, b) => a.amount - b.amount);

  const workflowSteps = [
    {
      title: "Will Generate Detailed Specifications",
      description: "Comprehensive technical specs with requirements and implementation guidelines",
      videoSrc: "/videos/specs.webm",
      icon: FileText,
      color: "#00FF88"
    },
    {
      title: "Will Generate Production Code",
      description: "Production-ready code with comprehensive integration tests",
      videoSrc: "/videos/code.webm",
      icon: Code,
      color: "#FF6B35"
    },
    {
      title: "Will Automate Pull Requests",
      description: "Automatic maintenance through PR comments - fix and test seamlessly",
      videoSrc: "/videos/pr.webm",
      icon: GitPullRequest,
      color: "#8B5CF6"
    },
    {
      title: "Will Keep Documentation Updated",
      description: "Real-time documentation sync that evolves with your code",
      videoSrc: "/videos/docs.webm",
      icon: BookOpen,
      color: "#F59E0B"
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-md flex items-start justify-center z-[100] overflow-y-auto p-4 pt-8">
      {/* Animated background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl max-w-6xl w-full mt-4 mb-8 overflow-hidden border border-gray-700">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 p-2 bg-gray-800/80 hover:bg-gray-700 rounded-lg transition-colors border border-gray-600"
        >
          <X className="w-5 h-5 text-gray-300" />
        </button>

        <div className="relative p-6 lg:p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 rounded-2xl mb-4 shadow-2xl animate-pulse">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-black mb-3 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              What ShredsAI will Do Next?
            </h2>
            <p className="text-base text-gray-300">
              Subscribe To Start Development Cycle
            </p>
          </div>

          {/* Workflow Steps with Videos */}
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {workflowSteps.map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <div key={index} className="relative bg-gray-950 rounded-2xl overflow-hidden shadow-2xl hover:shadow-cyan-500/20 transition-all duration-300 group border border-gray-800">
                    {/* Video */}
                    <div className="relative w-full h-48">
                      <video
                        className="w-full h-full object-cover"
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                      >
                        <source src={step.videoSrc} type="video/webm" />
                      </video>
                      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/80 via-black/60 to-transparent"></div>
                    </div>

                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 z-10 p-4">
                      <div className="flex items-start gap-3">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-cyan-500/30"
                          style={{ backgroundColor: step.color }}
                        >
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-sm font-bold text-white mb-1 leading-tight">
                            {step.title}
                          </h3>
                          <p className="text-xs text-gray-300 leading-tight">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Interval Selector */}
          <div className="flex justify-center mb-8">
            {intervals.length > 1 ? (
              <div className="inline-flex rounded-xl border border-cyan-500/30 bg-gray-800/50 backdrop-blur-md p-1.5">
                {intervals.map((interval) => (
                  <button
                    key={interval}
                    type="button"
                    className={`px-6 py-2 text-sm font-bold rounded-lg transition-all duration-300 ${
                      activeInterval === interval
                        ? 'bg-gradient-to-r from-cyan-500 to-purple-600 text-white shadow-lg shadow-cyan-500/50'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                    }`}
                    onClick={() => setActiveInterval(interval)}
                  >
                    {interval.charAt(0).toUpperCase() + interval.slice(1)}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* Pricing Plans - High-Tech Dark Design */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-cyan-500"></div>
                <div className="absolute inset-0 animate-ping rounded-full border-2 border-purple-500 opacity-20"></div>
              </div>
            </div>
          ) : sortedProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {sortedProducts.map((plan) => {
                const packageDetails = getPackageDetails(plan);
                const IconComponent = packageDetails.icon;

                return (
                  <div
                    key={plan.id}
                    className={`relative group ${plan.popular ? 'transform scale-105 mt-4' : ''}`}
                  >
                    {/* Glow effect */}
                    <div className={`absolute -inset-1 bg-gradient-to-r ${packageDetails.gradient} rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition duration-300`}></div>

                    <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl border border-gray-700 overflow-visible shadow-xl">
                      {/* Animated background pattern */}
                      <div className="absolute inset-0 opacity-5">
                        <div className="absolute inset-0" style={{
                          backgroundImage: `radial-gradient(circle at 2px 2px, ${packageDetails.accentColor} 1px, transparent 0)`,
                          backgroundSize: '32px 32px'
                        }}></div>
                      </div>

                      {/* Popular Badge */}
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
                          <div className="bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 text-black text-xs font-black px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 animate-pulse">
                            <Star className="w-3.5 h-3.5 fill-current" />
                            <span>MOST POPULAR</span>
                          </div>
                        </div>
                      )}

                      <div className="relative z-10 p-6">
                        {/* Header */}
                        <div className="text-center mb-5">
                          <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${packageDetails.gradient} rounded-2xl mb-3 shadow-xl`}
                            style={{ boxShadow: `0 10px 40px ${packageDetails.accentColor}40` }}
                          >
                            <IconComponent className="w-8 h-8 text-white" />
                          </div>
                          <h3 className="text-2xl font-black text-white mb-1">
                            {plan.name}
                          </h3>
                          <p className="text-xs font-semibold uppercase tracking-wider"
                            style={{ color: packageDetails.accentColor }}
                          >
                            {packageDetails.tagline}
                          </p>
                        </div>

                        {/* Price */}
                        <div className="text-center mb-6">
                          <div className="flex items-baseline justify-center mb-2">
                            <span className="text-4xl font-black bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                              {formatCurrency(plan.amount, plan.currency)}
                            </span>
                            <span className="text-gray-400 ml-2 text-sm font-medium">
                              /{plan.interval}
                            </span>
                          </div>
                          <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-800/50 rounded-full border border-gray-700">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                            <span className="text-xs text-gray-400">Auto-renewable</span>
                          </div>
                        </div>

                        {/* Features */}
                        <div className="mb-6 space-y-2.5">
                          {packageDetails.features.map((feature, featureIndex) => (
                            <div key={featureIndex} className="flex items-start gap-2.5 group/feature">
                              <div className="w-5 h-5 rounded-md bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center flex-shrink-0 mt-0.5 border border-gray-600 group-hover/feature:border-cyan-500/50 transition-colors">
                                <Check className="w-3 h-3" style={{ color: packageDetails.accentColor }} />
                              </div>
                              <span className="text-sm text-gray-300 leading-relaxed group-hover/feature:text-white transition-colors">
                                {feature}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Divider */}
                        <div className="relative mb-6">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-700"></div>
                          </div>
                          <div className="relative flex justify-center">
                            <span className="px-3 text-xs text-gray-500 bg-gray-900 uppercase tracking-wider">
                              All Features Included
                            </span>
                          </div>
                        </div>

                        {/* Tech Features */}
                        <div className="mb-6 grid grid-cols-2 gap-2">
                          <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-2 text-center">
                            <Code className="w-4 h-4 mx-auto mb-1 text-cyan-400" />
                            <span className="text-xs text-gray-400">Full Code</span>
                          </div>
                          <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-2 text-center">
                            <TestTube className="w-4 h-4 mx-auto mb-1 text-emerald-400" />
                            <span className="text-xs text-gray-400">Tests</span>
                          </div>
                          <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-2 text-center">
                            <BookOpen className="w-4 h-4 mx-auto mb-1 text-purple-400" />
                            <span className="text-xs text-gray-400">Docs</span>
                          </div>
                          <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-2 text-center">
                            <GitPullRequest className="w-4 h-4 mx-auto mb-1 text-pink-400" />
                            <span className="text-xs text-gray-400">PR Auto</span>
                          </div>
                        </div>

                        {/* CTA Button */}
                        <div className="relative">
                          <div className={`absolute -inset-0.5 bg-gradient-to-r ${packageDetails.gradient} rounded-xl blur opacity-40 group-hover:opacity-70 transition duration-300`}></div>
                          <button
                            className={`relative w-full bg-gradient-to-r ${packageDetails.gradient} hover:shadow-2xl text-white py-3.5 text-base font-bold rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2 border border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed`}
                            onClick={() => handleSelectProduct(plan)}
                            disabled={selectingProduct !== null}
                            style={{
                              boxShadow: plan.popular ? `0 10px 40px ${packageDetails.accentColor}60` : undefined
                            }}
                          >
                            {selectingProduct === plan.priceId ? (
                              <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Processing...</span>
                              </>
                            ) : (
                              <>
                                {plan.popular && <Crown className="w-5 h-5" />}
                                <span>Subscribe Now</span>
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                              </>
                            )}
                          </button>
                        </div>

                        {/* Price per credit indicator */}
                        <div className="mt-3 text-center">
                          <p className="text-xs text-gray-500">
                            Cancel anytime • Instant access
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-800 rounded-xl mb-3">
                <AlertCircle className="w-6 h-6 text-gray-500" />
              </div>
              <p className="text-gray-400 text-sm">No subscription plans available at the moment.</p>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-6 text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span>Secure Payment</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-cyan-400" />
                <span>Instant Activation</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-purple-400" />
                <span>Full Support</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedSubscriptionModalDark;
